import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useTranslate } from '~/shared/context/use-translate';

import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import { CommentComposer } from './CommentComposer';
import { CommentThreadCard } from './CommentThreadCard';
import { isCommentsScopeCurrent, useCommentsScope } from './comments-scope-context';
import type { Comment, CommentsRailProps } from './types';
import { useCommentRailLayout } from './use-comment-rail-layout';

const EMPTY_ACTIVE_COMMENTS: readonly Comment[] = [];

export const CommentsRail: FunctionComponent<CommentsRailProps> = ({
    architectureId,
    commentResolutionDisabled = false,
    itemId,
    language,
    newThreadCreationDisabled = false,
    version,
}) => {
    const translate = useTranslate();
    const scope = useCommentsScope();
    const railContentRef = useRef<HTMLDivElement | null>(null);
    const cardRefsRef = useRef<Map<string, HTMLElement>>(new Map());
    const activeComments = useActiveThreadStore((state) =>
        isCommentsScopeCurrent(state.commentClassificationScope, scope)
            ? (state.commentClassification?.activeComments ?? EMPTY_ACTIVE_COMMENTS)
            : EMPTY_ACTIVE_COMMENTS,
    );
    const pendingTarget = useActiveThreadStore((state) => state.pendingTarget);
    const cancelPending = useActiveThreadStore((state) => state.cancelPending);

    useCommentRailLayout({
        cardRefs: cardRefsRef.current,
        comments: activeComments,
        itemId,
        railContentRef,
    });

    const registerCard = useCallback((id: string, el: HTMLElement | null) => {
        if (is.null(el)) {
            cardRefsRef.current.delete(id);
            return;
        }
        cardRefsRef.current.set(id, el);
    }, []);

    useEffect(() => {
        if (!newThreadCreationDisabled || is.null(pendingTarget)) return;
        cancelPending();
    }, [cancelPending, newThreadCreationDisabled, pendingTarget]);

    return (
        <div
            ref={railContentRef}
            className="relative min-h-full flex-1 overflow-hidden px-s"
            data-testid="comments-rail"
        >
            {activeComments.map((comment) => (
                <CommentThreadCard
                    key={comment.id}
                    architectureId={architectureId}
                    comment={comment}
                    commentResolutionDisabled={commentResolutionDisabled}
                    itemId={itemId}
                    language={language}
                    registerCard={registerCard}
                    version={version}
                />
            ))}
            {!newThreadCreationDisabled && !is.null(pendingTarget) ? (
                <CommentComposer
                    key={pendingTarget.commentId}
                    architectureId={architectureId}
                    itemId={itemId}
                    language={language}
                    registerCard={registerCard}
                    version={version}
                />
            ) : null}
            {activeComments.length === 0 && (is.null(pendingTarget) || newThreadCreationDisabled) ? (
                <p className="px-m py-xl text-center text-xs text-gray-500">
                    {translate('pages.job-architecture-v2.comments.empty.body')}
                </p>
            ) : null}
        </div>
    );
};
